import React, {useEffect, useRef, useState} from "react";

const closeAnimationDurationMs = 240;

export function ServiceCheckDialog({open, onClose, className = "", children}) {
  const [shouldRender, setShouldRender] = useState(open);
  const [isVisible, setIsVisible] = useState(false);
  const previouslyFocusedRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      setIsVisible(false);
      return;
    }

    setShouldRender(true);
    const animationFrame = requestAnimationFrame(() => setIsVisible(true));

    return () => cancelAnimationFrame(animationFrame);
  }, [open]);

  useEffect(() => {
    if (open || !shouldRender) {
      return;
    }

    const timeout = setTimeout(() => setShouldRender(false), closeAnimationDurationMs);

    return () => clearTimeout(timeout);
  }, [open, shouldRender]);

  useEffect(() => {
    if (!shouldRender) {
      return;
    }

    previouslyFocusedRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onCloseRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);

      if (previouslyFocusedRef.current instanceof HTMLElement) {
        previouslyFocusedRef.current.focus({preventScroll: true});
      }
    };
  }, [shouldRender]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black px-4 py-20 backdrop-blur-sm transition-opacity ease-out ${
        isVisible ? "duration-150" : "duration-[240ms]"
      } ${
        isVisible ? "bg-opacity-60 opacity-100" : "bg-opacity-0 opacity-0"
      }`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onCloseRef.current();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={`relative m-4 w-full min-w-[95%] max-w-[95%] rounded-lg bg-white font-sans text-base font-light leading-relaxed text-blue-gray-500 antialiased shadow-2xl transition-all ease-out md:w-5/6 md:min-w-[83.333333%] md:max-w-[83.333333%] 2xl:w-3/4 2xl:min-w-[75%] 2xl:max-w-[75%] ${
          isVisible ? "duration-150" : "duration-[240ms]"
        } ${
          isVisible ? "translate-y-0 scale-100 opacity-100" : "-translate-y-3 scale-[0.98] opacity-0"
        } ${className}`}
      >
        {children}
      </div>
    </div>
  );
}
